import React from 'react';

import { Footer, Header } from '$partials/global';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').GbbpbProfile}
 */
const GbbpbProfile = (props) => {
    const page = props.data.page;

    const listFormatter = new Intl.ListFormat(
        'en',
        {
            type: 'conjunction',
            style: 'long'
        }
    );

    return (
        <>
            <Header {...props?.data?.header} />

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    {/* Header */}
                    <div className='col-wrapper-breakout flex flex-col sm:flex-row sm:flex-wrap gap-48 sm:gap-0 mb-8 md:mb-16 pb-8 pt-24 rounded-lg bg-db-blue text-neutral-00'>
                        <div className='w-full sm:w-8/12 md:w-7/12 col-padding'>
                            <h1 className='type-style-sans-display'>
                                {page.parent?.title}
                            </h1>

                            {page?.parent?.sponsorMedia?.image?.src && (
                                <div
                                    aria-hidden='true'
                                    className='w-full sm:w-4/8 md:w-3/7 mt-40 pointer-events-none select-none'>
                                    <p className='type-style-single-line-eyebrow mb-16'>Sponsored By</p>

                                    <img
                                        alt={page.parent.sponsorMedia.image.alt || 'Sponsor logo'}
                                        className='w-full'
                                        src={page.parent.sponsorMedia.image.src} />
                                </div>
                            )}
                        </div>

                        <div className='w-full sm:w-4/12 md:w-5/12 col-padding pointer-events-none select-none'>
                            {page?.parent?.headerMedia?.image?.px2 && (
                                <img
                                    alt={page?.parent.headerMedia.image.alt || 'Header media image'}
                                    className='h-full w-full object-cover object-center'
                                    src={page.parent.headerMedia.image.px2} />
                            )}
                        </div>
                    </div>

                    <div className='col-wrapper-breakout flex flex-col gap-32 p-8 md:p-16 rounded-lg bg-surface-panel-blue'>
                        <div className='flex flex-col gap-16'>
                            <div className='flex flex-wrap p-8 md:p-16 rounded-lg bg-neutral-00'>
                                {/* Image */}
                                <div className='w-full sm:w-4/12 md:w-3/12 rounded-lg overflow-hidden pointer-events-none select-none'>
                                    {page?.media?.image?.px2 && (
                                        <img
                                            alt={page.media.image.alt || 'Company logo'}
                                            className='h-full w-full object-cover object-center'
                                            src={page.media.image.px2} />
                                    )}
                                </div>

                                <div className='w-full sm:w-8/12 md:w-9/12 mt-36 sm:mt-0'>
                                    {/* Display Rank */}
                                    <div className='w-full col-padding'>
                                        <div className='sm:px-8 md:px-16'>
                                            <p className='type-style-serif-h2 mb-8 sm:mb-0'>
                                                {page?.displayRank}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <div className='w-full col-padding'>
                                        <div className='sm:px-8 md:px-16'>
                                            <p className='type-style-serif-h2 mb-8 sm:mb-0'>
                                                {listFormatter.format(page?.people?.map((p) => p?.name))}
                                            </p>

                                            <div className='flex gap-40'>
                                                <h1 className='type-style-sans-h3'>{page?.title}</h1>
                                                <p className='type-style-sans-h3'>£{page?.value} billion</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* People */}
                            <ol className='flex flex-col gap-16'>
                                {page?.people?.map((person, idx) => {
                                    const bgClass = idx === 0 ? 'bg-neutral-00' : 'bg-tint-light-60';

                                    return (
                                        <li
                                            className={`w-full rounded-lg ${bgClass}`}
                                            key={idx}>
                                            <dl className='flex flex-wrap'>
                                                <div className='w-full border-b border-border-default p-16'>
                                                    <dt className='sr-only'>Name</dt>

                                                    <dd>
                                                        <h2 className='type-style-serif-h4'>
                                                            {person?.name}
                                                        </h2>
                                                    </dd>
                                                </div>

                                                {person?.details?.length > 0 && person.details.map((item, idx) => {
                                                    return (
                                                        <div
                                                            className={`w-full sm:w-4/12 md:w-2/12 p-16 border-b md:border-b-0 border-border-default ${idx === 0 ? '' : 'sm:border-l'}`}
                                                            key={idx}>
                                                            <dt className='mb-8 type-style-single-line-eyebrow'>
                                                                {item?.label}
                                                            </dt>

                                                            <dd className='type-style-sans-body-s'>
                                                                <div
                                                                    className='rich-text type-style-sans-body-s'
                                                                    dangerouslySetInnerHTML={{__html: item?.value || '—' }}/>
                                                            </dd>
                                                        </div>
                                                    );
                                                })}
                                            </dl>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>

                        {/* Long Bio*/}
                        <div className='w-full sm:w-9/12 md:w-6/12'>
                            <div
                                className='rich-text rich-text--compact'
                                dangerouslySetInnerHTML={{ __html: page?.longBio }} />
                        </div>

                        {/* Sponsor */}
                        <div className='w-full'>
                            <div className='relative p-16 rounded-lg border border-border-default overflow-hidden'>
                                <h2 className='type-style-sans-h1 mb-8'>
                                    {page?.parent?.title}
                                </h2>

                                <p className='type-style-sans-body mb-40'>
                                    Find out more about the entrepreneurs who have built the UK&rsquo;s billion-pound businesses.
                                </p>

                                <p>
                                    <a
                                        className='button-primary-xl'
                                        href={page?.parent?.url}>
                                        See the full list
                                    </a>
                                </p>

                                <div className='absolute bottom-0 right-0 h-full w-3/12'>
                                    {page?.parent?.headerMedia?.image?.px2 && (
                                        <img
                                            alt={page.parent.headerMedia.image.alt || 'Header media image'}
                                            className='h-full w-full object-cover object-center pointer-events-none select-none'
                                            src={page.parent.headerMedia.image.px2} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer {...props?.data?.footer} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default GbbpbProfile;
